import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { format, parseISO } from 'date-fns';
import { Pencil, Trash2, Save, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select } from '../../ui/newSelect';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Badge } from '../../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { Actions as IDX } from '../../../store/actions/indexationActions';
import Breadcrumb from '../../Common/Breadcrumb';
import { toggleAlertMessage } from '../../../store/action';

const SOURCES = ['UK_CPIH', 'UK_CPI', 'UK_RPI'];

const IndexationRatesList = ({
  getIndexationRates,
  upsertIndexationRate,
  deleteIndexationRate,
  rates,
  loading,
  showMessage,
}) => {
  const [source, setSource] = useState('UK_CPIH');
  const [edit, setEdit] = useState(null);
  const [val, setVal] = useState('');

  useEffect(() => {
    getIndexationRates(source);
  }, [getIndexationRates, source]);

  const rows = rates?.rates || [];

  const onSave = async (periodEndIso, row) => {
    if (edit === periodEndIso) {
      const p = val === '' ? row?.percent : Number(val);
      const r = await upsertIndexationRate(source, periodEndIso, { percent: p });
      if (r?.ok) {
        setEdit(null);
        showMessage(true, 'Rate updated', 'success', 2000);
        getIndexationRates(source);
      } else {
        showMessage(true, (r && r.error && r.error.message) || 'Update failed', 'error', 4000);
      }
    } else {
      setEdit(periodEndIso);
      setVal(String(row?.percent != null ? row.percent : ''));
    }
  };

  const onDelete = async (periodEndIso) => {
    const r = await deleteIndexationRate(source, periodEndIso);
    if (r?.ok) {
      showMessage(true, 'Override removed', 'success', 2000);
    } else if (r?.code === 'cannot_delete_auto_rate') {
      showMessage(true, 'Only manual overrides can be removed', 'error', 4000);
    } else {
      showMessage(true, 'Delete failed', 'error', 4000);
    }
  };

  return (
    <div className="p-4 max-w-5xl mx-auto">
      <Breadcrumb items={[{ label: 'Indexation rates' }]} />
      <Card>
        <CardHeader className="flex flex-wrap gap-4 items-end justify-between">
          <CardTitle>ONS / indexation rates</CardTitle>
          <div className="min-w-[200px]">
            <Label>Source</Label>
            <Select
              value={source}
              onChange={(v) => {
                setSource(v);
                setEdit(null);
              }}
              options={SOURCES}
            />
          </div>
        </CardHeader>
        <CardContent>
          {loading && !rows.length ? (
            <div className="py-8 flex justify-center">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            <div className="border border-border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period end</TableHead>
                    <TableHead>Percent</TableHead>
                    <TableHead>Manual</TableHead>
                    <TableHead>Fetched at</TableHead>
                    <TableHead className="w-[100px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-6">
                        No rates. Flow may be disabled.
                      </TableCell>
                    </TableRow>
                  )}
                  {rows.map((r) => {
                    const p = r.periodEndIso || r.periodEnd;
                    return (
                      <TableRow key={p}>
                        <TableCell>
                          {p
                            ? String(p).length >= 8
                              ? format(parseISO(String(p).slice(0, 10)), 'yyyy-MM')
                              : p
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {edit === p ? (
                            <Input
                              className="w-24 h-8"
                              value={val}
                              onChange={(e) => setVal(e.target.value)}
                            />
                          ) : (
                            `${r.percent != null ? r.percent : '—'}%`
                          )}
                        </TableCell>
                        <TableCell>
                          {r.manualOverride ? (
                            <Badge>Manual</Badge>
                          ) : (
                            <span className="text-muted-foreground">Auto</span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {r.fetchedAt || '—'}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => onSave(p, r)}>
                              {edit === p ? <Save size={16} /> : <Pencil size={16} />}
                            </Button>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      disabled={!r.manualOverride}
                                      onClick={() => onDelete(p)}
                                    >
                                      <Trash2 size={16} />
                                    </Button>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  Remove manual override (auto ONS rows cannot be deleted)
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const mapState = (s) => ({
  rates: s.indexation?.rates,
  loading: s.indexation?.loading?.rates,
});

const mapDispatch = (d) => ({
  getIndexationRates: (s) => d(IDX.getIndexationRates(s)),
  upsertIndexationRate: (a, p, b) => d(IDX.upsertIndexationRate(a, p, b)),
  deleteIndexationRate: (a, p) => d(IDX.deleteIndexationRate(a, p)),
  showMessage: (...a) => d(toggleAlertMessage(...a)),
});

export default connect(mapState, mapDispatch)(IndexationRatesList);
